import router from '@/router';
import Debug from 'debug'
import { injectable, inject } from 'inversify';
import { getOffsetTop, getPositionTop, sectionVisible } from '@/app/shared/utils/content-utils';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import { TocItem } from '@/app/shared/models';
import {
  SectionInteractionEvent,
  SectionInteractedEventName
} from '@/app/statistics/events/section-interaction-events';
import { SectionNavigationService, SectionNavigationServiceSymbol } from './section-navigation-service';
import sectionStoreModule from '@/app/statistics/state/section-state';
import aspectStoreModule from '@/app/aspect/apsect-state';
import { isSectionDescendantOf } from './get-parent';

const debug = Debug('section-scroll-service')

@injectable()
export class SectionScrollService {
  constructor(
    @inject(EventStreamSymbol)
    private eventStream: EventStream,
    @inject(SectionNavigationServiceSymbol)
    private sectionNavigationService: SectionNavigationService) {
  }

  public listenScroll(sections: TocItem[]): void {
    if (sections.length === 0) {
      return;
    }

    debug('listenScroll', debug.enabled ? sections.map(x => x.id) : '');

    const $scrollElement: any = $('.scrollable-box');
    const mainContentSelector = '.main-content';
    const toAdd = this.calculateToAdd($scrollElement, mainContentSelector)

    $scrollElement.on('scroll', () => {
      if (!sectionStoreModule.autoScrollInProgress) {
        debug('scroll', sections);
        const localToAdd = this.calculateToAdd($scrollElement, mainContentSelector)
        this.onScroll(sections, $scrollElement, localToAdd, mainContentSelector);
      }
    });

    // initial scroll check
    this.onScroll(sections, $scrollElement, toAdd, mainContentSelector, false);
  }

  private calculateToAdd($scrollElement: any, mainContentSelector: string) {
    const toAdd = $scrollElement.height() - (getOffsetTop(mainContentSelector) - ($('#header') as any).height());
    debug('calculate toAdd', 'scrollElement height:', $scrollElement.height(),
          'offsetTop:', getOffsetTop(mainContentSelector),
          'header height: ', ($('#header') as any).height(),
          'toAdd', toAdd)
    return toAdd
  }

  private onScroll(
    sections: TocItem[],
    $scrollElement: any,
    toAdd: number,
    mainContentSelector: string,
    hasScrollBar: boolean = true
  ): void {

    debug('onScroll', debug.enabled ? sections.map(x => x.id) : '')

    let currentPassedSection: TocItem|null = null;
    for (let i = 0; i < sections.length; i++) {
      const passed = this.calculateSectionScroll(sections, i, $scrollElement, toAdd, mainContentSelector, hasScrollBar);
      if (passed) {
        currentPassedSection = sections[i];
      } else {
        debug('navigateToSection in loop', currentPassedSection ? currentPassedSection.id : 'NONE')
        this.navigateToSection(currentPassedSection);
      }
    }

    if (currentPassedSection) {
      debug('current passed section', currentPassedSection.id)
    }

    debug('navigateToSection after', currentPassedSection ? currentPassedSection.id : 'NONE')
    this.navigateToSection(currentPassedSection);
  }

  private navigateToSection(currentPassedSection: null | TocItem): void {
    debug('check in navigateToSection', currentPassedSection?.id)
    if (currentPassedSection !== null
          && sectionVisible(currentPassedSection.id)
          && currentPassedSection.id !== this.sectionNavigationService.currentSectionId
          && currentPassedSection.id !== router.currentRoute.query.section) {
      debug('navigateToSection', currentPassedSection.id)
      router.push({ query: { section: currentPassedSection.id } }).then(() => {});
    }
  }

  private calculateSectionScroll(
    sections: TocItem[],
    i: number,
    $scrollElement: any,
    toAdd: number,
    mainContentSelector: string,
    hasScrollBar: boolean = true
  ): boolean {

    const section = sections[i];
    debug('calculateSectionScroll', section.id)

    const scrollTop = $scrollElement.scrollTop() + toAdd;
    const position = getPositionTop(`#${section.id}`);
    const passed = position !== 0 && scrollTop > position;

    debug('bare scrollTop', $scrollElement.scrollTop(), 'translated scrollTop', $scrollElement.scrollTop() + toAdd)

    if (position === 0 || !passed) {
      debug('not passed or position 0, returning', section.id,
            'scrollTop:', scrollTop - toAdd,
            'position:', position,
            'passed: ', passed)
      return passed;
    }

    const isThereNextSection = sections.length > i + 1;
    const nextSection = isThereNextSection && $(`#${sections[i + 1].id}`).is(':visible')
      ? sections[i + 1]
      : null;
    let nextPosition = 0;
    if (nextSection !== null) {
      nextPosition = getPositionTop(`#${nextSection.id}`);
    } else {
      nextPosition = $(mainContentSelector).height() as number;
    }

    let percent = Math.ceil(100 * (scrollTop - position) / (nextPosition - position));
    if (percent >= 100) {
      percent = 100;
    }

    if (percent >= 90) {
      // to workaround scroll calculation bugs at end of the page
      percent = 100
    }

    debug('section scroll', section.id,
          'percent:', percent,
          'scrollTop:', scrollTop,
          'toAdd:', toAdd,
          'position:', position,
          '(scrollTop - position)', scrollTop - position,
          'nextPosition:', nextPosition,
          'current section:', sectionStoreModule.currentSectionId)

    // TODO: with the new API
    /*if (completion.sections[section] > percent) {
      return passed;
    }

    completion.sections[section] = percent;*/

    debug('check whether send scroll change', section.id, 'current: ', sectionStoreModule.currentSectionId)

    const isDescendantOfCurrent = sectionStoreModule.currentSectionId
          && section.id !== sectionStoreModule.currentSectionId
          && isSectionDescendantOf(section.id,
                                   sectionStoreModule.currentSectionId,
                                   aspectStoreModule.sectionsById)
    debug(`'${section.id}' is descendant of current '${sectionStoreModule.currentSectionId}'`, isDescendantOfCurrent)

    if (section.id === sectionStoreModule.currentSectionId || isDescendantOfCurrent || !hasScrollBar) {
      debug('send scroll change', section.id)
      this.eventStream.dispatch({
        name: SectionInteractedEventName,
        payload: {
          scrolledPercent: percent,
          sectionId: section.id
        }
      } as SectionInteractionEvent);
    }

    return passed;
  }
}

export const SectionScrollServiceSymbol = Symbol.for('SectionScrollService');
